import React, { useState, useEffect } from 'react';
import { Mic, MicOff, X, Sparkles } from 'lucide-react';

const MicPreferencePrompt = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already been asked
    const settings = JSON.parse(localStorage.getItem('virgo_settings') || '{}');
    if (!settings.astraeaMicPreferenceAsked) {
      // Small delay for smoother UX after login
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleChoice = (alwaysOn) => {
    const settings = JSON.parse(localStorage.getItem('virgo_settings') || '{}');
    settings.astraeaMicAlwaysOn = alwaysOn;
    settings.astraeaMicPreferenceAsked = true;
    localStorage.setItem('virgo_settings', JSON.stringify(settings));

    setIsVisible(false);
    if (onComplete) {
      onComplete(alwaysOn);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-cyan-500/30 p-6 max-w-md mx-4 animate-slideUp">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Welcome to Astraea</h2>
            <p className="text-cyan-400 text-sm">Your AI assistant</p>
          </div>
        </div>

        {/* Message */}
        <div className="mb-6">
          <p className="text-slate-300 mb-3">
            Would you like the microphone always on so you can say <span className="text-blue-400 font-semibold">"Hey Astraea"</span> anytime to get help?
          </p>
          <p className="text-slate-500 text-sm">
            You can always use the mic button in the chat to talk to Astraea, regardless of this setting.
          </p>
        </div>

        {/* Options */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleChoice(true)}
            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white transition-all shadow-lg hover:shadow-cyan-500/30 hover:scale-105"
          >
            <Mic className="w-8 h-8" />
            <span className="font-semibold">Yes, always on</span>
            <span className="text-xs text-cyan-200">Say "Hey Astraea" anytime</span>
          </button>

          <button
            onClick={() => handleChoice(false)}
            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-700 hover:bg-slate-600 text-white transition-all hover:scale-105"
          >
            <MicOff className="w-8 h-8" />
            <span className="font-semibold">No thanks</span>
            <span className="text-xs text-slate-400">Use mic button instead</span>
          </button>
        </div>

        {/* Skip link */}
        <button
          onClick={() => handleChoice(false)}
          className="mt-4 w-full text-center text-slate-500 hover:text-slate-400 text-sm transition-colors"
        >
          Skip for now
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default MicPreferencePrompt;
